import {createApi} from '@reduxjs/toolkit/query/react';

import {customAxiosBaseQuery} from '../../API/customAxiosBaseQuery';
import {storeCashierAPI} from '../../API/api';
import {
	CashActionType,
	CashierActionType,
	CashierShiftExitType,
	RequestBillingListType,
	RequestCreateCashierOrderType,
	RequestEditCashierOrderType,
	ResponseAccountListType,
	ResponseBillingListType,
	ResponsePrroStatusListType,
	ZReportType,
} from '../../types/CashierSliceStateType';
import {BaseResponseType} from '../../types/TypesStore';
import {AuthFormValues} from '../../Components/AuthCashierPopup/AuthCashierPopup';
import {
	RequestPaymentCreateType,
	RequestPaymentListType,
	ResponseCheckListType,
	ResponsePaymentListType,
} from '../../types/TypesUserOrders';

export const depotCashierApi = createApi({
	reducerPath: 'prroApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'SupplierList',
		'x-report',
		'z-report',
		'z-report-list',
		'account-list',
		'billing-list',
		'payment-list',
		'check-list',
	],
	endpoints: (builder) => ({
		getLogisticList: builder.query<ResponsePrroStatusListType, void>({
			query: () => ({
				func: storeCashierAPI.getPRROStatus,
				args: [],
			}),
			providesTags: ['SupplierList'],
		}),
		getXReport: builder.query<BaseResponseType & {htmlreport: string}, string>({
			query: (shiftId) => ({
				func: storeCashierAPI.getXReport,
				args: [shiftId],
			}),
			providesTags: ['x-report'],
		}),
		getZReport: builder.query<BaseResponseType & {htmlreport: string}, string>({
			query: (id) => ({
				func: storeCashierAPI.getZReport,
				args: [id],
			}),
			providesTags: ['z-report'],
		}),
		getZReportList: builder.query<
			BaseResponseType & {reports: ZReportType[]},
			void
		>({
			query: () => ({
				func: storeCashierAPI.getZReportList,
				args: [],
			}),
			providesTags: ['z-report-list'],
		}),
		getBillingList: builder.query<
			ResponseBillingListType,
			RequestBillingListType
		>({
			query: (data) => ({
				func: storeCashierAPI.getBillingList,
				args: [data],
			}),
			providesTags: ['billing-list'],
		}),
		createCashOrder: builder.mutation<
			BaseResponseType,
			RequestCreateCashierOrderType
		>({
			query: (data) => ({
				func: storeCashierAPI.getCreateCashOrder,
				args: [data],
			}),
			invalidatesTags: ['billing-list'],
		}),
		editCashOrder: builder.mutation<
			BaseResponseType,
			RequestEditCashierOrderType
		>({
			query: (data) => ({
				func: storeCashierAPI.getEditCashOrder,
				args: [data],
			}),
			invalidatesTags: ['billing-list'],
		}),
		deleteCashOrder: builder.mutation<BaseResponseType, number>({
			query: (id) => ({
				func: storeCashierAPI.getDeleteCashOrder,
				args: [id],
			}),
			invalidatesTags: ['billing-list'],
		}),
		getAccountsList: builder.query<ResponseAccountListType, void>({
			query: () => ({
				func: storeCashierAPI.getAccountsList,
				args: [],
			}),
			providesTags: ['account-list'],
		}),
		getPaymentList: builder.query<
			ResponsePaymentListType,
			RequestPaymentListType
		>({
			query: (data) => ({
				func: storeCashierAPI.getPaymentList,
				args: [data],
			}),
			providesTags: ['payment-list'],
		}),
		getBonusCheckList: builder.query<ResponseCheckListType, string>({
			query: (month) => ({
				func: storeCashierAPI.getBonusCheckList,
				args: [month],
			}),
			providesTags: ['check-list'],
		}),
		createBonusCheck: builder.mutation<BaseResponseType, number>({
			query: (id) => ({
				func: storeCashierAPI.bonusCheckCreate,
				args: [id],
			}),
			invalidatesTags: ['check-list'],
		}),
		createManyBonusCheck: builder.mutation<
			BaseResponseType & {edited: string; error: string},
			number[]
		>({
			query: (ids) => ({
				func: storeCashierAPI.bonusCheckCreateMany,
				args: [ids],
			}),
			invalidatesTags: ['check-list'],
		}),
		deletePayment: builder.mutation<BaseResponseType, number>({
			query: (id) => ({
				func: storeCashierAPI.getDeletePayment,
				args: [id],
			}),
			invalidatesTags: ['payment-list'],
		}),
		createPayment: builder.mutation<
			BaseResponseType & {id: number},
			RequestPaymentCreateType
		>({
			query: (data) => ({
				func: storeCashierAPI.getCreatePayment,
				args: [data],
			}),
			invalidatesTags: ['payment-list'],
		}),
		verifyPayment: builder.mutation<BaseResponseType & {money: string}, string>(
			{
				query: (id) => ({
					func: storeCashierAPI.getVerifyPayment,
					args: [id],
				}),
				invalidatesTags: ['payment-list'],
			},
		),
		exitCasher: builder.mutation<BaseResponseType, CashierActionType>({
			query: (props) => ({
				func: storeCashierAPI.getExitCasher,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
		authCasher: builder.mutation<BaseResponseType, AuthFormValues>({
			query: (props) => ({
				func: storeCashierAPI.getAuthCasher,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
		openCasherShift: builder.mutation<BaseResponseType, CashierActionType>({
			query: (props) => ({
				func: storeCashierAPI.getOpenCasherShift,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
		closeCasherShift: builder.mutation<BaseResponseType, CashierShiftExitType>({
			query: (props) => ({
				func: storeCashierAPI.getCloseCasherShift,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
		cashDeposit: builder.mutation<BaseResponseType, CashActionType>({
			query: (props) => ({
				func: storeCashierAPI.getCashDeposit,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
		cashWithdrawal: builder.mutation<BaseResponseType, CashActionType>({
			query: (props) => ({
				func: storeCashierAPI.getCashWithdrawal,
				args: [props],
			}),
			invalidatesTags: ['SupplierList'],
		}),
	}),
});

export const {
	useGetLogisticListQuery,
	useLazyGetXReportQuery,
	useLazyGetZReportQuery,
	useLazyGetZReportListQuery,
	useExitCasherMutation,
	useAuthCasherMutation,
	useOpenCasherShiftMutation,
	useCloseCasherShiftMutation,
	useCashDepositMutation,
	useCashWithdrawalMutation,
	useGetBillingListQuery,
	useCreateCashOrderMutation,
	useEditCashOrderMutation,
	useDeleteCashOrderMutation,
	useGetAccountsListQuery,
	useGetPaymentListQuery,
	useCreatePaymentMutation,
	useVerifyPaymentMutation,
	useGetBonusCheckListQuery,
	useCreateBonusCheckMutation,
	useCreateManyBonusCheckMutation,
} = depotCashierApi;
